import { ApolloError } from "@apollo/client";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import client from "client/apollo";

import { RootState } from "@/data/store";
import { ParsedQuestionBank } from "@/features/question-bank-file-import/data";
import { MoodleParser } from "@/features/question-bank-file-import/parsers/moodle-parser";
import { QTIParser } from "@/features/question-bank-file-import/parsers/qti-parser";
import {
  LibraryQuestionFragment,
  LibraryQuestionFragmentDoc,
  PublishImportedQuestionsDocument,
  PublishImportedQuestionsInput,
  PublishImportedQuestionsMutation,
  PublishImportedQuestionsMutationVariables,
  QuestionSourceFileFragment,
  RegisterQuestionSourceFileDocument,
  RegisterQuestionSourceFileMutation,
  RegisterQuestionSourceFileMutationVariables,
  UpdateQuestionSourceFileDocument,
  UpdateQuestionSourceFileMutation,
  UpdateQuestionSourceFileMutationVariables,
} from "@/generated/graphql";

import { FileParsedQuestion } from "./types";

// TODO REMOVE THIS DANGEROUS ASSUMPTION
const GENERAL_TAG_CATEGORY_ID = "1";

export const loadFiles = createAsyncThunk<
  FileParsedQuestion[][],
  File[],
  { state: RootState }
>("questionImport/loadFiles", async (payload, thunkAPI) => {
  const filesBanks: ParsedQuestionBank[][] = await Promise.all(
    payload.map(async (file) => {
      let parsed;
      if (!parsed && (await QTIParser.maybeParsable(file))) {
        parsed = await QTIParser.parse(
          file,
          (filesCount) => thunkAPI.dispatch(addProcessingFiles(filesCount)),
          (filesCount) => thunkAPI.dispatch(addProcessedFiles(filesCount))
        );
      }
      if (!parsed && (await MoodleParser.maybeParsable(file))) {
        parsed = await MoodleParser.parse(
          file,
          (filesCount) => thunkAPI.dispatch(addProcessingFiles(filesCount)),
          (filesCount) => thunkAPI.dispatch(addProcessedFiles(filesCount))
        );
      }
      if (parsed && file?.type === "application/zip") {
        return parsed.map((f) => ({
          ...f,
          fileOrigin: {
            ...f.fileOrigin,
            filepath: `${file.name}/${f.fileOrigin.filepath}`,
          },
        }));
      }
      return parsed ?? [];
    })
  );

  return filesBanks;
});

export const updateFilename = createAsyncThunk<
  QuestionSourceFileFragment | undefined,
  { fileOriginId: string; filename: string },
  {
    state: RootState;
    rejectValue: { filename: string | undefined; error: Error };
  }
>("questionImport/updateFilename", async (payload, thunkAPI) => {
  const { questionImport } = thunkAPI.getState();
  const { fileOriginId, filename } = payload;
  const bank = questionImport.importedQuestions.find(
    (bank) => bank.fileOrigin.fileOriginId === fileOriginId
  );
  const { id, filepath } = bank?.fileOrigin ?? {};
  if (!filepath) {
    return thunkAPI.rejectWithValue({
      filename: bank?.fileOrigin.filename,
      error: new Error("filepath cannot be empty"),
    });
  }
  try {
    if (id) {
      const { data } = await client.mutate<
        UpdateQuestionSourceFileMutation,
        UpdateQuestionSourceFileMutationVariables
      >({
        mutation: UpdateQuestionSourceFileDocument,
        variables: {
          input: {
            id: id,
            filename: filename,
          },
        },
      });
      return data?.updateQuestionSourceFile;
    } else {
      const { data } = await client.mutate<
        RegisterQuestionSourceFileMutation,
        RegisterQuestionSourceFileMutationVariables
      >({
        mutation: RegisterQuestionSourceFileDocument,
        variables: {
          input: {
            filename: filename,
            filepath: filepath,
          },
        },
      });
      return data?.registerQuestionSourceFile;
    }
  } catch (e) {
    return thunkAPI.rejectWithValue({
      filename: bank?.fileOrigin.filename,
      error: e as Error,
    });
  }
});

export interface PublishImportedQuestionsArgs {
  fileOriginIds: string[];
  subjectId: string;
  assessmentId: string;
}

export const publishImportedQuestions = createAsyncThunk<
  LibraryQuestionFragment[],
  PublishImportedQuestionsArgs,
  {
    state: RootState;
    rejectValue: { filename: string | undefined; error: string };
  }
>("questionImport/publishQuestions", async (payload, thunkAPI) => {
  const { fileOriginIds, subjectId, assessmentId } = payload;

  const importedQuestionFiles = thunkAPI
    .getState()
    .questionImport.importedQuestions.filter((parsed) =>
      fileOriginIds.includes(parsed.fileOrigin.fileOriginId)
    );

  if (importedQuestionFiles.length === 0) return [];

  try {
    const results = await Promise.all(
      importedQuestionFiles.map(async (file) => {
        const input: PublishImportedQuestionsInput = {
          subjectId,
          assessmentId,
          sourceFile: {
            id: file.fileOrigin.id,
            filename: file.fileOrigin.filename,
            filepath: file.fileOrigin.filepath,
          },
          tags: file.tags?.map((tag) => ({
            name: tag,
            categoryId: GENERAL_TAG_CATEGORY_ID,
          })),
          questions: file.questions.flatMap((question) => {
            // import only supported types
            if (!question.questionType) return [];
            return [
              {
                questionType: question.questionType,
                points: question.points,
                shuffle: question.shuffle,
                promptDoc: question.prompt ?? "",
                promptImage: question.promptImage,
                shortPrompt: question.shortPrompt ?? "",
                feedback: question.feedback,
                fields: question.fields,
              },
            ];
          }),
        };

        const { data } = await client.mutate<
          PublishImportedQuestionsMutation,
          PublishImportedQuestionsMutationVariables
        >({
          mutation: PublishImportedQuestionsDocument,
          variables: {
            input,
          },
          update: (cache, { data }) => {
            const newQuestions = data?.publishImportedQuestions;
            if (newQuestions) {
              cache.modify({
                fields: {
                  libraryQuestions(questionRefs = []) {
                    const newQuestionRefs = newQuestions.map((question) => {
                      return cache.writeFragment({
                        fragment: LibraryQuestionFragmentDoc,
                        fragmentName: "LibraryQuestion",
                        data: question,
                      });
                    });
                    return [...questionRefs, ...newQuestionRefs];
                  },
                },
              });
            }
          },
        });
        return data?.publishImportedQuestions ?? [];
      })
    );

    return results.flat();
  } catch (error) {
    if (error instanceof ApolloError) {
      return thunkAPI.rejectWithValue({
        filename: undefined,
        error: "We couldn't import some files. Please try again later.",
      });
    }
    throw error;
  }
});

export const addProcessingFiles = createAction(
  "questionImport/addProcessingFiles",
  (filesCount: number) => ({ payload: { filesCount } })
);

export const addProcessedFiles = createAction(
  "questionImport/addProcessedFiles",
  (filesCount: number) => ({ payload: { filesCount } })
);
